import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ColumnFilterRow, applyColumnFilters } from "../../../Components/Common/DashboardKit";
import useTableSort from "../../../Components/Common/DashboardKit/useTableSort";
import SortableTh from "../../../Components/Common/DashboardKit/SortableTh";
import exportRowsToCsv from "../../../Components/Common/DashboardKit/exportCsv";
import {
    Container, Row, Col, Card, CardBody, CardHeader,
    Input, Button, Badge, Spinner, Table, InputGroup, InputGroupText,
    Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label,
} from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { DashboardPageHeader, TableSkeleton } from '../../../Components/Common/DashboardKit';
import { usePermissions } from '../../../hooks/usePermissions';
import api from '../../../api';
// Hotfix 4 (post-R12) — preview pane on the dedicated workflow page.
// Same modal the /document-management table opens via the eye icon, so
// "preview" UX is consistent across both DMS entry points (see release
// notes for why both pages exist).
import DmsViewModal from '../ECN/DmsViewModal';
// Round DMS-UAT-3 — task sub-rows open the modern Task window (legacy
// double-click parity: WinForms opened the Task form with details /
// reassign / mark-complete / logging; the modern equivalent is
// /task-management/view-task/:taskId).
import { useNavigate } from 'react-router-dom';
// Round DMS-UAT-1 (2026) — Issue 3: sticky Doc-ID / Action columns.
import '../../../assets/styles/dmsTables.css';

/**
 * Awaiting Release (DMS Manager / Documents Ready for Release permission)
 *
 * Lists all documents at Status = 1 (Approved – Awaiting Release).
 * Allows a permission holder to release them to Status = 2 (Approved & Released).
 *
 * Backend (Phase 2.2):
 *   GET  /api/dms/awaiting-release
 *   POST /api/dms/{id}/release  body { releasedBy, implementationDate }
 *
 * The backend ALSO checks the permission on the POST, so a tampered FE
 * would still get 400 from the API.
 *
 * Round DMS-UAT-1 (2026):
 *   • Issue 13 — the release POST can now return 409 with the list of
 *     incomplete tasks; the confirm modal shows a warning with the task
 *     list, Cancel stays the default, and an explicit "Release Anyway"
 *     (server re-validated + audited) resends with the override flag.
 *   • Issue 3/4/5 — sticky Doc-ID + Action columns, author names, and a
 *     friendly labeled export (Status included, Manager removed, dates in
 *     an Excel-parseable format).
 */

const docTypeLabel = (t) =>
    ({ 1: 'SOP', 2: 'WI', 3: 'FCD', 4: 'ECN', 5: 'LIT' })[t] || `Type ${t}`;

// Excel-friendly timestamp (Issue 4): "YYYY-MM-DD HH:MM" converts cleanly
// via Format Cells → Date, unlike raw ISO-with-T or locale strings.
const excelDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// Round DMS-UAT-1 — Issue 4: friendly labeled export columns. Author is the
// NAME; the EmpID is its own column; Status is included; the Manager column
// the user called out as not useful is intentionally omitted.
// Round DMS-UAT-2 — legacy parity: Status is the DmsStatus label from the
// backend, and the export interleaves one sub-row per task under each
// document (Title = task description, Task ID, Assignee + EmpID, Status =
// Completed/Assigned) exactly like the legacy grid's Export button.
const EXPORT_COLUMNS = [
    { label: "Document",       get: (r) => r._isTask ? "" : (r.docId || "") },
    { label: "Type",           get: (r) => r._isTask ? "" : docTypeLabel(r.docType) },
    { label: "Title",          get: (r) => r._isTask ? (r.description || "") : (r.docTitle || "") },
    { label: "Revision",       get: (r) => r._isTask ? "" : (r.rev ?? "") },
    { label: "Task ID",        get: (r) => r._isTask ? (r.taskId ?? "") : "" },
    { label: "Name",           get: (r) => r._isTask ? (r.assigneeName || "") : (r.authorName || "") },
    { label: "EmpID",          get: (r) => r._isTask ? (r.assigneeEmpId ?? "") : (r.author ?? "") },
    { label: "Status",         get: (r) => r.status || "" },
    { label: "Due Date",       get: (r) => r._isTask ? excelDate(r.due) : "" },
    { label: "Creation Date",  get: (r) => r._isTask ? "" : excelDate(r.createdTime) },
];

// Interleave doc rows with their task sub-rows (legacy merged-table shape).
const toMergedExportRows = (docs) => {
    const out = [];
    (docs || []).forEach((d) => {
        out.push(d);
        (d.tasks || []).forEach((t) => out.push({ ...t, _isTask: true }));
    });
    return out;
};

const todayIso = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const AwaitingRelease = () => {
    // Round R1 (2026) — switched the defensive in-page gate from a
    // perm-only check (hasAccess("Documents Ready for Release")) to the
    // module gate (canAccessModule("dms.release")). The module gate
    // resolves on the backend to:
    //     HasPerm("Documents Ready for Release") OR RoleId=8 (DMS Manager
    //     / Operations) OR RoleId=4 (IT Admin).
    // This matches the gate now applied to the /dms/awaiting-release
    // route via <RoleProtected requireModule="dms.release">. The two
    // gates stay in lockstep — change one, change the other.
    const { canAccessModule, isLoading: permLoading } = usePermissions();
    const canRelease = canAccessModule('dms.release');

    const authUser = useMemo(() => {
        try { return JSON.parse(sessionStorage.getItem('authUser') || '{}'); }
        catch { return {}; }
    }, []);
    const empId = Number(authUser.uid);
    // Round DMS-UAT-3 — for opening task sub-rows in the Task window.
    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [search, setSearch] = useState('');
    // Round 34 — EPO-style per-column filters + click-to-sort (the global
    // search box is replaced by the filter row).
    const [columnFilters, setColumnFilters] = useState({});
    const tblColumns = React.useMemo(() => [
        { id: "docId",    label: "Doc ID", type: "text", get: (r) => r.docId || "" },
        { id: "type",     label: "Type",   type: "text", get: (r) => docTypeLabel(r.docType) },
        { id: "docTitle", label: "Title",  type: "text", get: (r) => r.docTitle || "" },
        { id: "rev",      label: "Rev",    type: "text", get: (r) => String(r.rev ?? "") },
        // Round DMS-UAT-1 — Issue 5: filter/sort by author NAME.
        { id: "author",   label: "Author", type: "text", get: (r) => r.authorName || String(r.author ?? "") },
        // Round DMS-UAT-2 — legacy parity: Status column carries the
        // DmsStatus label (e.g. "ECR Action Items Issued"), same as the
        // legacy Documents Awaiting Release grid.
        { id: "status",   label: "Status", type: "text", get: (r) => r.status || "" },
        { id: "createdTime", label: "Created", type: "date", get: (r) => r.createdTime || "" },
        { id: "act",      label: "Action", type: "none" },
    ], []);

    // release-confirm modal
    const [confirmRow, setConfirmRow] = useState(null);
    const [implDate, setImplDate] = useState(todayIso());
    const [releasing, setReleasing] = useState(false);
    // Round DMS-UAT-1 — Issue 13: when the backend answers 409 the release
    // is blocked; the modal switches to a warning listing the incomplete
    // tasks. Cancel remains the default; "Release Anyway" resends with
    // overrideIncompleteTasks + the typed reason (server-side re-validated
    // and audited).
    const [incompleteTasks, setIncompleteTasks] = useState(null);
    const [overrideReason, setOverrideReason] = useState('');

    // Hotfix 4 (post-R12) — preview state. Same DmsViewModal the main doc
    // list uses; when set, the modal opens for that dmsId.
    const [previewDmsId, setPreviewDmsId] = useState(null);

    const loadAll = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const res = await api.get('dms/awaiting-release');
            setRows(res.data || []);
        } catch (err) {
            setLoadError(err?.response?.data?.error || err?.message || 'Failed to load.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!permLoading && canRelease) loadAll();
        else if (!permLoading) setLoading(false);
    }, [permLoading, canRelease, loadAll]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) =>
            [r.docId, r.docTitle, docTypeLabel(r.docType)]
                .filter(Boolean).join(' ').toLowerCase().includes(q)
        );
    }, [rows, search]);
    const filteredBase = applyColumnFilters(filtered, tblColumns, columnFilters);
    const { sorted: view, sort, toggleSort } = useTableSort(filteredBase, tblColumns);

    const openConfirm = (row) => {
        setConfirmRow(row);
        setImplDate(todayIso());
        setIncompleteTasks(null);
        setOverrideReason('');
    };
    const closeConfirm = () => {
        if (releasing) return;
        setConfirmRow(null);
        setIncompleteTasks(null);
        setOverrideReason('');
    };

    const handleRelease = async (overrideIncomplete = false) => {
        if (!confirmRow) return;
        setReleasing(true);
        try {
            const body = {
                releasedBy: empId,
                implementationDate: implDate || null,
            };
            if (overrideIncomplete) {
                body.overrideIncompleteTasks = true;
                body.overrideReason = overrideReason || null;
            }
            await api.post(`dms/${confirmRow.dmsId}/release`, body);
            setRows((prev) => prev.filter((r) => r.dmsId !== confirmRow.dmsId));
            toast.success(`Released ${confirmRow.docId}.`);
            setConfirmRow(null);
            setIncompleteTasks(null);
            setOverrideReason('');
        } catch (err) {
            // Round DMS-UAT-1 — Issue 13: 409 means "incomplete tasks".
            // Switch the modal into the warning state instead of toasting;
            // nothing has been released and Cancel is fully safe.
            if (err?.response?.status === 409 && err?.response?.data?.incompleteTasks) {
                setIncompleteTasks(err.response.data.incompleteTasks);
            } else {
                toast.error(err?.response?.data?.error || 'Failed to release.');
            }
        } finally {
            setReleasing(false);
        }
    };

    // Permission gate (defensive — route guard also handles this)
    if (!permLoading && !canRelease) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Card>
                        <CardBody className="text-center p-5">
                            <i className="ri-shield-cross-line text-warning" style={{ fontSize: 48 }} />
                            <h5 className="mt-3">Access Denied</h5>
                            <p className="text-muted mb-0">
                                You need the "Documents Ready for Release" permission,
                                the DMS Manager role, or IT Admin to view this page.
                            </p>
                        </CardBody>
                    </Card>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-send-plane-line"
                    title="Awaiting Release"
                    subtitle="Documents fully approved and ready to release"
                    tone="green"
                    breadcrumb={[
                        { label: "Dashboards", to: "/" },
                        { label: "DMS", to: "/workflows?wf=dms" },
                        { label: "Awaiting Release" },
                    ]}
                />

                <Row>
                    <Col xs={12}>
                        <Card>
                            <CardHeader className="d-flex flex-wrap gap-2 align-items-center">
                                <div className="flex-grow-1">
                                    <h5 className="card-title mb-0">
                                        <i className="ri-checkbox-circle-line align-bottom me-1 text-success"></i>
                                        Approved — awaiting release
                                    </h5>
                                    <div className="text-muted small mt-1">
                                        Releasing moves a document from Status 1 to Status 2 (Approved &amp; Released)
                                        and sets the implementation date.
                                    </div>
                                </div>
                                {/* Round 34 — search box replaced by the per-column filter row */}
                                <Button color="light" onClick={loadAll} disabled={loading} title="Reload">
                                    <i className="ri-refresh-line align-bottom"></i>
                                </Button>
                            </CardHeader>

                            <CardBody>
                                {(loading || permLoading) && (
                                    <div className="table-responsive">
                                        <Table className="align-middle table-nowrap mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Doc ID</th><th>Type</th><th>Title</th><th>Rev</th>
                                                    <th>Author</th><th>Status</th><th>Created</th><th className="text-end">Action</th>
                                                </tr>
                                                <ColumnFilterRow columns={tblColumns} filters={columnFilters} onChange={setColumnFilters} />
                                            </thead>
                                            <tbody>
                                                <TableSkeleton rows={6} columns={8} />
                                            </tbody>
                                        </Table>
                                    </div>
                                )}

                                {!loading && !permLoading && loadError && (
                                    <div className="alert alert-danger d-flex align-items-center">
                                        <i className="ri-error-warning-line me-2 fs-5"></i>
                                        <div className="flex-grow-1">{loadError}</div>
                                        <Button size="sm" color="danger" outline onClick={loadAll}>Retry</Button>
                                    </div>
                                )}

                                {!loading && !permLoading && !loadError && filtered.length === 0 && (
                                    <div className="text-center py-5 text-muted">
                                        <i className="ri-emotion-happy-line" style={{ fontSize: 36 }}></i>
                                        <div className="mt-2">
                                            {search ? 'No matches.' : 'Nothing awaiting release.'}
                                        </div>
                                    </div>
                                )}

                                {!loading && !permLoading && !loadError && filtered.length > 0 && (
                            <>
                            {/* Round DMS-UAT-1 — Issue 4: friendly labeled
                                columns exported for the CURRENT FILTERED set
                                (author name + EmpID + Status; Manager column
                                removed per feedback). */}
                            <div className="d-flex justify-content-end mb-2">
                                <button
                                    type="button"
                                    className="btn btn-soft-primary btn-sm"
                                    title="Exports the rows currently shown (all filters applied), with one sub-row per task like the legacy export"
                                    onClick={() => exportRowsToCsv("dms-awaiting-release.csv", EXPORT_COLUMNS, toMergedExportRows(view))}
                                >
                                    <i className="ri-download-2-line align-bottom me-1" />Export CSV ({view.length})
                                </button>
                            </div>
<div className="table-responsive">
                                        <Table className="align-middle mb-0 dms-sticky-table" hover>
                                            <thead className="table-light">
                                                <tr>
                                                    {tblColumns.slice(0, 7).map((c) => (
                                                        <SortableTh key={c.id} col={c} sort={sort} onToggle={toggleSort} />
                                                    ))}
                                                    <th className="text-end">Action</th>
                                                </tr>
                                                {/* Round DMS-UAT-2 — the per-column filter row now also
                                                    renders on the LOADED table (previously it only
                                                    existed in the loading skeleton). */}
                                                <ColumnFilterRow columns={tblColumns} filters={columnFilters} onChange={setColumnFilters} />
                                            </thead>
                                            <tbody>
                                                {view.map((r) => (
                                                    <React.Fragment key={r.dmsId}>
                                                    {/* Round DMS-UAT-3 — clicking the document row opens
                                                        the preview (legacy double-click → release window
                                                        parity; all release-window actions live in the
                                                        modal's Document Actions bar + Edit). */}
                                                    <tr
                                                        style={{ cursor: "pointer" }}
                                                        title="Open document"
                                                        onClick={() => setPreviewDmsId(r.dmsId)}
                                                    >
                                                        <td><code>{r.docId}</code></td>
                                                        <td><Badge color="info" pill>{docTypeLabel(r.docType)}</Badge></td>
                                                        <td className="dms-title-cell" title={r.docTitle || ""}>
                                                            {r.docTitle || <span className="text-muted">—</span>}
                                                        </td>
                                                        <td>{r.rev ?? '—'}</td>
                                                        <td>
                                                            {r.authorName
                                                                ? (<>
                                                                    {r.authorName}
                                                                    <div className="text-muted small">ID: {r.author}</div>
                                                                  </>)
                                                                : (r.author ?? '—')}
                                                        </td>
                                                        {/* Round DMS-UAT-2 — legacy DmsStatus label,
                                                            e.g. "ECR Action Items Issued" */}
                                                        <td>
                                                            <Badge color={r.statusCode === 56 ? "success" : "warning"} pill>
                                                                {r.status || '—'}
                                                            </Badge>
                                                        </td>
                                                        <td>{r.createdTime ? new Date(r.createdTime).toLocaleDateString() : '—'}</td>
                                                        <td className="text-end">
                                                            {/* Hotfix 4 (post-R12) — preview eye icon
                                                                opens DmsViewModal, matching the affordance
                                                                on /document-management. Sits to the left
                                                                of Release so it's the lower-risk default
                                                                tap target. */}
                                                            <Button
                                                                color="light"
                                                                size="sm"
                                                                onClick={(e) => { e.stopPropagation(); setPreviewDmsId(r.dmsId); }}
                                                                title="Preview document"
                                                                className="me-2"
                                                            >
                                                                <i className="ri-eye-line align-bottom"></i>
                                                            </Button>
                                                            <Button color="success" size="sm" onClick={(e) => { e.stopPropagation(); openConfirm(r); }}>
                                                                <i className="ri-send-plane-fill align-bottom me-1"></i>Release
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                    {/* Round DMS-UAT-2 — legacy parity: one sub-row per
                                                        task under its document (Documents Awaiting
                                                        Release grid in the WinForms dashboard shows the
                                                        same structure via RefreshDmsTask). Assignee is
                                                        name + EmpID; Status is Completed/Assigned.
                                                        Round DMS-UAT-3 — clicking the row opens the
                                                        modern Task window (/task-management/view-task),
                                                        matching the legacy double-click that opened the
                                                        Task form with details / reassign / complete /
                                                        logging. */}
                                                    {(r.tasks || []).map((t) => (
                                                        <tr
                                                            key={`t-${t.taskId}-${t.assigneeEmpId}`}
                                                            className="table-light"
                                                            style={{ cursor: "pointer" }}
                                                            title={`Open task #${t.taskId}`}
                                                            onClick={() => navigate(`/task-management/view-task/${t.taskId}`)}
                                                        >
                                                            <td></td>
                                                            <td className="text-muted small">Task</td>
                                                            <td className="dms-title-cell small" title={t.description || ""}>
                                                                <i className="ri-corner-down-right-line me-1 text-muted"></i>
                                                                {t.description || `Task #${t.taskId}`}
                                                                <span className="text-muted"> (#{t.taskId})</span>
                                                            </td>
                                                            <td></td>
                                                            <td className="small">
                                                                {t.assigneeName
                                                                    ? <>{t.assigneeName} <span className="text-muted">(ID: {t.assigneeEmpId})</span></>
                                                                    : t.assigneeEmpId}
                                                            </td>
                                                            <td>
                                                                <Badge color={t.complete ? "success" : "secondary"} pill>
                                                                    {t.status}
                                                                </Badge>
                                                            </td>
                                                            <td className="small">
                                                                {t.due ? new Date(t.due).toLocaleDateString() : '—'}
                                                            </td>
                                                            <td className="text-end">
                                                                <Button
                                                                    color="light"
                                                                    size="sm"
                                                                    title={`Open task #${t.taskId}`}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        navigate(`/task-management/view-task/${t.taskId}`);
                                                                    }}
                                                                >
                                                                    <i className="ri-external-link-line align-bottom"></i>
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    </React.Fragment>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                            </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <Modal isOpen={!!confirmRow} toggle={closeConfirm} backdrop={releasing ? 'static' : true}>
                <ModalHeader toggle={closeConfirm}>Release document</ModalHeader>
                <ModalBody>
                    {confirmRow && (
                        <>
                            <p>
                                Releasing <code>{confirmRow.docId}</code> — <strong>{confirmRow.docTitle}</strong>.
                                This sets the document to "Approved &amp; Released".
                            </p>
                            <FormGroup>
                                <Label for="implDate">Implementation Date</Label>
                                <Input
                                    type="date"
                                    id="implDate"
                                    value={implDate}
                                    onChange={(e) => setImplDate(e.target.value)}
                                    disabled={releasing}
                                />
                            </FormGroup>

                            {/* Round DMS-UAT-1 — Issue 13: incomplete-task
                                warning. The list comes from the server's 409
                                response; nothing was released. Cancel is
                                safe; Release Anyway is an explicit, audited
                                override re-validated on the server. */}
                            {incompleteTasks && (
                                <div className="alert alert-warning mt-2 mb-0">
                                    <div className="fw-semibold mb-2">
                                        <i className="ri-error-warning-line align-bottom me-1"></i>
                                        This document has {incompleteTasks.length} incomplete task{incompleteTasks.length === 1 ? '' : 's'}:
                                    </div>
                                    <ul className="mb-2 ps-3">
                                        {incompleteTasks.map((t) => (
                                            <li key={t.taskId}>
                                                {t.description || `Task #${t.taskId}`}
                                                {t.dueDate && (
                                                    <span className="text-muted"> — due {new Date(t.dueDate).toLocaleDateString()}</span>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="small mb-2">
                                        Complete these tasks before releasing, or release anyway with a reason
                                        (the override is recorded in the revision log).
                                    </div>
                                    <Input
                                        type="textarea"
                                        rows={2}
                                        maxLength={200}
                                        placeholder="Reason for releasing with open tasks (recorded in the audit trail)"
                                        value={overrideReason}
                                        onChange={(e) => setOverrideReason(e.target.value)}
                                        disabled={releasing}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={closeConfirm} disabled={releasing}>Cancel</Button>
                    {!incompleteTasks ? (
                        <Button color="success" onClick={() => handleRelease(false)} disabled={releasing || !implDate}>
                            {releasing ? (<><Spinner size="sm" /> Releasing…</>) : 'Release'}
                        </Button>
                    ) : (
                        <Button
                            color="warning"
                            onClick={() => handleRelease(true)}
                            disabled={releasing || !implDate}
                            title="Release even though tasks are incomplete (audited)"
                        >
                            {releasing ? (<><Spinner size="sm" /> Releasing…</>) : 'Release Anyway'}
                        </Button>
                    )}
                </ModalFooter>
            </Modal>

            {/* Hotfix 4 (post-R12) — preview pane. The modal is reused
                from /document-management so authorization gates + action
                bar (R4) + Edit/Print buttons (R12) all behave identically.
                Releasing from here happens via the green Release button
                in the table row, not the action bar — the modal's own
                Release button (when visible) goes through the same
                endpoint, so both paths are safe. */}
            {previewDmsId && (
                <DmsViewModal
                    isOpen={!!previewDmsId}
                    toggle={() => setPreviewDmsId(null)}
                    dmsId={previewDmsId}
                />
            )}

            <ToastContainer closeButton={false} limit={3} />
        </div>
    );
};

export default AwaitingRelease;
